import { forwardRef } from "react";
import ReactSelect, { SingleValue } from "react-select";

export interface SelectProps {
  options?: { value: string; label: string }[];
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  onChange?: (
    e: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => void;
  value?: { value: string; label: string };
  isDisabled?: boolean;
  maxWidth?: string;
  minWidth?: string;
  inputId?: string;
  "aria-label"?: string;
  variant?: "light" | "dark";
}

const DARK = {
  bg: "rgba(255, 255, 255, 0.08)",
  bgHover: "rgba(255, 255, 255, 0.16)",
  border: "rgba(255, 255, 255, 0.24)",
  borderHover: "rgba(255, 255, 255, 0.4)",
  muted: "rgba(255, 255, 255, 0.7)",
};

const COLORS = {
  panel: "var(--chakra-colors-bg-panel)",
  border: "var(--chakra-colors-border)",
  borderHover: "var(--chakra-colors-border-emphasized)",
  focus: "var(--chakra-colors-brand-focus-ring)",
  text: "var(--chakra-colors-fg)",
  muted: "var(--chakra-colors-fg-muted)",
  subtle: "var(--chakra-colors-bg-subtle)",
  solid: "var(--chakra-colors-brand-solid)",
  solidText: "var(--chakra-colors-brand-contrast)",
  disabledBg: "var(--chakra-colors-bg-muted)",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- preexistente, ver ticket de tipado
export const Select = forwardRef<any, SelectProps>((props, ref) => {
  const { variant = "light", ...selectProps } = props;
  const dark = variant === "dark";
  return (
    <ReactSelect
      {...selectProps}
      value={props.value === undefined ? null : props.value}
      ref={ref}
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      styles={{
        container: (base) => ({
          ...base,
          width: "100%",
          minWidth: props.minWidth ?? "160px",
          maxWidth: props.maxWidth ?? "100%",
        }),
        control: (base, state) =>
          dark
            ? {
                ...base,
                minHeight: 36,
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                backgroundColor: state.isFocused ? DARK.bgHover : DARK.bg,
                borderColor: state.isFocused ? DARK.borderHover : DARK.border,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: DARK.bgHover,
                  borderColor: DARK.borderHover,
                },
              }
            : {
                ...base,
                minHeight: 40,
                borderRadius: 6,
                fontSize: 14,
                backgroundColor: state.isDisabled
                  ? COLORS.disabledBg
                  : COLORS.panel,
                borderColor: state.isFocused ? COLORS.focus : COLORS.border,
                boxShadow: state.isFocused
                  ? `0 0 0 1px ${COLORS.focus}`
                  : "none",
                "&:hover": {
                  borderColor: state.isFocused
                    ? COLORS.focus
                    : COLORS.borderHover,
                },
              },
        valueContainer: (base) => ({ ...base, paddingInline: 12 }),
        placeholder: (base) => ({
          ...base,
          color: dark ? DARK.muted : COLORS.muted,
        }),
        singleValue: (base) => ({
          ...base,
          color: dark ? "white" : COLORS.text,
        }),
        input: (base) => ({ ...base, color: dark ? "white" : COLORS.text }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (base) => ({
          ...base,
          color: dark ? DARK.muted : COLORS.muted,
          paddingInline: 8,
        }),
        clearIndicator: (base) => ({
          ...base,
          color: dark ? DARK.muted : COLORS.muted,
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: COLORS.panel,
          zIndex: 40,
          borderRadius: 8,
          fontSize: 14,
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
          border: `1px solid ${COLORS.border}`,
        }),
        option: (base, state) => ({
          ...base,
          cursor: "pointer",
          backgroundColor: state.isSelected
            ? COLORS.solid
            : state.isFocused
              ? COLORS.subtle
              : COLORS.panel,
          color: state.isSelected ? COLORS.solidText : COLORS.text,
        }),
      }}
    />
  );
});
